import { skipToken } from "@reduxjs/toolkit/query";
import { automationIdSchema } from "@repo/app-types/entities/automation";
import { automationArtifactIdSchema } from "@repo/app-types/entities/automationArtifact";
import { automationRunIdSchema } from "@repo/app-types/entities/automationRun";
import { playbookIdSchema } from "@repo/app-types/entities/playbook";
import { formatRunTime } from "@repo/ui/automations/automationPresentation";
import { resolveArtifactSentAt } from "@repo/ui/automations/automationTables";
import type { FC } from "react";
import { useAutomationByIdQuery } from "../../api/entities/automation.js";
import {
  useAutomationArtifactByIdQuery,
  useMyAgentResultByIdQuery,
} from "../../api/entities/automationArtifact.js";
import { useAutomationRunByIdQuery } from "../../api/entities/automationRun.js";
import { renderCrumbLabel } from "../../app/navigation/crumbLabel.js";

// Breadcrumb labels for the automations (Agents) pages: each fetches its own
// entity, shows a skeleton while loading, and settles on a generic label for
// invalid ids or failed fetches.

export const AutomationNameCrumbLabel: FC<{
  automationId: string | undefined;
}> = ({ automationId }) => {
  const parsedId = automationIdSchema.safeParse(automationId);
  const result = useAutomationByIdQuery(
    parsedId.success ? parsedId.data : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedId.success,
    result,
    buildText: (automation) => automation.name,
    fallbackText: "Agent",
  });
};

export const AutomationRunTimeCrumbLabel: FC<{
  automationId: string | undefined;
  runId: string | undefined;
}> = ({ automationId, runId }) => {
  const parsedAutomationId = automationIdSchema.safeParse(automationId);
  const parsedRunId = automationRunIdSchema.safeParse(runId);
  const result = useAutomationRunByIdQuery(
    parsedAutomationId.success && parsedRunId.success
      ? {
          automationId: parsedAutomationId.data,
          automationRunId: parsedRunId.data,
        }
      : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedAutomationId.success && parsedRunId.success,
    result,
    buildText: (run) => formatRunTime(run.started_at ?? run.created_at),
    fallbackText: "Run",
  });
};

export const AutomationArtifactTimeCrumbLabel: FC<{
  automationId: string | undefined;
  artifactId: string | undefined;
}> = ({ automationId, artifactId }) => {
  const parsedAutomationId = automationIdSchema.safeParse(automationId);
  const parsedArtifactId = automationArtifactIdSchema.safeParse(artifactId);
  const result = useAutomationArtifactByIdQuery(
    parsedAutomationId.success && parsedArtifactId.success
      ? {
          automationId: parsedAutomationId.data,
          automationArtifactId: parsedArtifactId.data,
        }
      : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedAutomationId.success && parsedArtifactId.success,
    result,
    buildText: (artifact) => formatRunTime(artifact.created_at),
    fallbackText: "Result",
  });
};

export const MyAgentResultTimeCrumbLabel: FC<{
  playbookId: string | undefined;
  artifactId: string | undefined;
}> = ({ playbookId, artifactId }) => {
  const parsedPlaybookId = playbookIdSchema.safeParse(playbookId);
  const parsedArtifactId = automationArtifactIdSchema.safeParse(artifactId);
  const result = useMyAgentResultByIdQuery(
    parsedPlaybookId.success && parsedArtifactId.success
      ? {
          automationArtifactId: parsedArtifactId.data,
          playbookId: parsedPlaybookId.data,
        }
      : skipToken,
  );
  return renderCrumbLabel({
    hasValidId: parsedPlaybookId.success && parsedArtifactId.success,
    result,
    buildText: (artifact) =>
      formatRunTime(resolveArtifactSentAt(artifact) ?? artifact.created_at),
    fallbackText: "Notification",
  });
};
